import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const SafetyChecklist = () => {
  const [checkedItems, setCheckedItems] = useState({});

  const checklistCategories = [
    {
      category: "Monthly Safety Checks",
      icon: "Calendar",
      color: "var(--color-primary)",
      items: [
        { id: "m1", text: "Test all GFCI outlets by pressing test button" },
        { id: "m2", text: "Check for warm or discolored outlets and switches" },
        { id: "m3", text: "Inspect visible wiring for damage or wear" },
        { id: "m4", text: "Test smoke detectors and replace batteries if needed" }
      ]
    },
    {
      category: "Quarterly Inspections",
      icon: "ClipboardCheck",
      color: "var(--color-accent)",
      items: [
        { id: "q1", text: "Check circuit breaker panel for corrosion or damage" },
        { id: "q2", text: "Inspect outdoor electrical equipment and weatherproofing" },
        { id: "q3", text: "Test emergency lighting systems" },
        { id: "q4", text: "Review electrical load on circuits" }
      ]
    },
    {
      category: "Annual Professional Review",
      icon: "UserCheck",
      color: "var(--color-success)",
      items: [
        { id: "a1", text: "Schedule comprehensive electrical safety inspection" },
        { id: "a2", text: "Update Certificate of Compliance if required" },
        { id: "a3", text: "Review and upgrade outdated electrical systems" },
        { id: "a4", text: "Plan for future electrical needs and upgrades" }
      ]
    }
  ];

  const handleCheckboxChange = (itemId) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev?.[itemId]
    }));
  };

  const getTotalChecked = () => {
    return Object.values(checkedItems)?.filter(Boolean)?.length;
  };

  const getTotalItems = () => {
    return checklistCategories?.reduce((sum, cat) => sum + cat?.items?.length, 0);
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-muted">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="font-headline font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-4">
            Homeowner Electrical Safety Checklist
          </h2>
          <p className="font-body text-base md:text-lg text-muted-foreground max-w-3xl mx-auto mb-6">
            Stay safe with regular electrical maintenance. Use this checklist to identify potential issues before they become emergencies.
          </p>
          <div className="inline-flex items-center gap-3 bg-card px-6 py-3 rounded-full shadow-sm">
            <Icon name="CheckCircle2" size={24} color="var(--color-success)" />
            <span className="font-accent text-lg">
              {getTotalChecked()} of {getTotalItems()} completed
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {checklistCategories?.map((category) => (
            <div key={category?.category} className="bg-card border border-border rounded-xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-lg"
                  style={{ backgroundColor: `${category?.color}15` }}
                >
                  <Icon name={category?.icon} size={24} color={category?.color} />
                </div>
                <h3 className="font-headline font-semibold text-lg md:text-xl text-foreground">
                  {category?.category}
                </h3>
              </div>

              <div className="space-y-4">
                {category?.items?.map((item) => (
                  <Checkbox
                    key={item?.id}
                    label={item?.text}
                    checked={checkedItems?.[item?.id] || false}
                    onChange={() => handleCheckboxChange(item?.id)}
                    className="text-sm"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 md:mt-12 bg-warning/10 border border-warning/20 rounded-xl p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Icon name="AlertTriangle" size={32} color="var(--color-warning)" />
            </div>
            <div>
              <h4 className="font-headline font-semibold text-lg md:text-xl text-foreground mb-2">
                When to Call a Professional
              </h4>
              <p className="font-body text-sm md:text-base text-muted-foreground mb-4">
                If you notice any of these warning signs, contact Gert immediately at 082 793 5785:
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  "Frequent circuit breaker trips",
                  "Burning smell from outlets or switches",
                  "Flickering or dimming lights",
                  "Warm or discolored outlets",
                  "Sparks when plugging in devices",
                  "Buzzing or humming sounds from electrical panel"
                ]?.map((warning, index) => (
                  <li key={index} className="flex items-center gap-2 font-body text-sm text-foreground">
                    <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                    <span>{warning}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SafetyChecklist;